import type { CSSProperties } from "react";

import { formatNumber } from "../../lib/format";
import { useTheme } from "../../themes/ThemeProvider";
import type { ThemeBankPalette } from "../../themes/types";

const MAX_SPRITES = 36;

const SPRITE_LAYOUT: ReadonlyArray<{
  x: number;
  y: number;
  size: number;
  rotate: number;
  delay: number;
}> = [
  { x: 48, y: 7, size: 22, rotate: -9, delay: 0 },
  { x: 38, y: 9, size: 21, rotate: 7, delay: 0.03 },
  { x: 59, y: 9, size: 23, rotate: 12, delay: 0.06 },
  { x: 28, y: 13, size: 22, rotate: -16, delay: 0.09 },
  { x: 70, y: 14, size: 20, rotate: 5, delay: 0.12 },
  { x: 48, y: 17, size: 24, rotate: -3, delay: 0.15 },
  { x: 36, y: 21, size: 22, rotate: 15, delay: 0.18 },
  { x: 61, y: 22, size: 21, rotate: -11, delay: 0.21 },
  { x: 24, y: 27, size: 19, rotate: 8, delay: 0.24 },
  { x: 75, y: 28, size: 20, rotate: -7, delay: 0.27 },
  { x: 48, y: 30, size: 23, rotate: 18, delay: 0.3 },
  { x: 35, y: 35, size: 21, rotate: -14, delay: 0.33 },
  { x: 61, y: 36, size: 22, rotate: 9, delay: 0.36 },
  { x: 27, y: 42, size: 20, rotate: -5, delay: 0.39 },
  { x: 74, y: 43, size: 19, rotate: 17, delay: 0.42 },
  { x: 48, y: 46, size: 24, rotate: -12, delay: 0.45 },
  { x: 36, y: 51, size: 22, rotate: 6, delay: 0.48 },
  { x: 61, y: 52, size: 20, rotate: -18, delay: 0.51 },
  { x: 24, y: 58, size: 19, rotate: 13, delay: 0.54 },
  { x: 75, y: 59, size: 21, rotate: -8, delay: 0.57 },
  { x: 48, y: 62, size: 23, rotate: 4, delay: 0.6 },
  { x: 35, y: 67, size: 20, rotate: -15, delay: 0.63 },
  { x: 62, y: 68, size: 22, rotate: 10, delay: 0.66 },
  { x: 28, y: 74, size: 19, rotate: 5, delay: 0.69 },
  { x: 72, y: 75, size: 20, rotate: -13, delay: 0.72 },
  { x: 48, y: 78, size: 24, rotate: 16, delay: 0.75 },
  { x: 37, y: 83, size: 21, rotate: -6, delay: 0.78 },
  { x: 60, y: 84, size: 20, rotate: 11, delay: 0.81 },
  { x: 25, y: 89, size: 18, rotate: -18, delay: 0.84 },
  { x: 75, y: 90, size: 19, rotate: 9, delay: 0.87 },
  { x: 48, y: 93, size: 23, rotate: -4, delay: 0.9 },
  { x: 34, y: 96, size: 18, rotate: 12, delay: 0.93 },
  { x: 62, y: 96, size: 18, rotate: -10, delay: 0.96 },
  { x: 42, y: 99, size: 17, rotate: 5, delay: 0.99 },
  { x: 55, y: 99, size: 17, rotate: -7, delay: 1.02 },
  { x: 49, y: 101, size: 16, rotate: 2, delay: 1.05 }
];

// Default rainbow palette — used when a theme doesn't ship its own
// `bank` slot. Matches the historical hardcoded values so every theme
// that pre-dated the theming slot looks identical.
const DEFAULT_BANK_PALETTE: ThemeBankPalette = {
  coinBase: "#ffd45a",
  coinRim: "#b8750f",
  coinFill: "rgba(255, 210, 63, 0.4)",
  coinGlow: "rgba(255, 157, 77, 0.26)",
  gumballPalette: ["#ff4fd8", "#75f6ff", "#ffe66d", "#95ff8f", "#ff9d4d", "#9f7aff"],
  gumballRim: "rgba(255,255,255,0.28)",
  gumballFill: "rgba(255, 79, 216, 0.42)",
  gumballGlow: "rgba(117, 246, 255, 0.24)"
};

interface GumballJarsProps {
  availableCoins: number;
  receivedGumballs: number;
  onReceivedClick: () => void;
}

export function GumballJars({
  availableCoins,
  receivedGumballs,
  onReceivedClick
}: GumballJarsProps) {
  const { theme } = useTheme();
  const palette = theme.assets.bank ?? DEFAULT_BANK_PALETTE;
  return (
    <section className="gumball-jars" aria-label="Gumball season jars">
      <div className="gumball-jars__intro">
        <span className="gumball-jars__eyebrow">Season stash</span>
        <h2>Watch your bank fill up</h2>
        <p>Coins are what you can spend. Gumballs are what teammates have given you.</p>
      </div>
      <div className="gumball-jars__shelf">
        <JarMetric
          kind="coins"
          label="Available coins"
          value={availableCoins}
          caption="coins left to spend"
          palette={palette}
        />
        <JarMetric
          kind="gumballs"
          label="Gumballs received"
          value={receivedGumballs}
          caption={
            receivedGumballs > 0
              ? "click to see who filled your jar"
              : "received gumballs will land here"
          }
          onClick={receivedGumballs > 0 ? onReceivedClick : undefined}
          palette={palette}
        />
      </div>
    </section>
  );
}

function JarMetric({
  kind,
  label,
  value,
  caption,
  onClick,
  palette
}: {
  kind: "coins" | "gumballs";
  label: string;
  value: number;
  caption: string;
  onClick?: () => void;
  palette: ThemeBankPalette;
}) {
  const clickable = onClick !== undefined;
  const content = (
    <>
      <div className="jar-metric__glass" aria-hidden="true">
        <div className="jar-metric__table-shadow" />
        <div className="jar-metric__lid">
          <span />
        </div>
        <div className="jar-metric__body">
          <div className="jar-metric__shoulder" />
          <div className="jar-metric__contents" style={contentsStyle(value, kind, palette)}>
            <div className="jar-metric__fill" />
            {SPRITE_LAYOUT.slice(0, visibleSpriteCount(value)).map((sprite, index) => (
              <span
                key={index}
                className={`jar-metric__sprite jar-metric__sprite--${kind}`}
                style={spriteStyle(sprite, index, kind, palette)}
              />
            ))}
          </div>
          <div className="jar-metric__glass-warp" />
          <div className="jar-metric__rim jar-metric__rim--top" />
          <div className="jar-metric__rim jar-metric__rim--bottom" />
          <div className="jar-metric__shine" />
        </div>
      </div>
      <div className="jar-metric__copy">
        <span className="jar-metric__label">{label}</span>
        <strong>{formatNumber(value)}</strong>
        <span>{caption}</span>
      </div>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        className={`jar-metric jar-metric--${kind} jar-metric--interactive`}
        onClick={onClick}
        aria-label={`View who gave you ${formatNumber(value)} gumball${value === 1 ? "" : "s"}`}
      >
        {content}
      </button>
    );
  }

  return <div className={`jar-metric jar-metric--${kind}`}>{content}</div>;
}

function visibleSpriteCount(value: number): number {
  if (value <= 0) {
    return 0;
  }
  if (value <= 10) {
    return value;
  }
  return Math.min(MAX_SPRITES, Math.max(12, Math.ceil(Math.sqrt(value) * 5)));
}

function fillLevel(value: number): number {
  if (value <= 0) {
    return 0;
  }
  return Math.min(88, Math.max(16, 17 + Math.sqrt(value) * 9));
}

function contentsStyle(
  value: number,
  kind: "coins" | "gumballs",
  palette: ThemeBankPalette
): CSSProperties {
  const fill = fillLevel(value);
  return {
    ["--jar-fill" as string]: `${fill}%`,
    ["--jar-fill-color" as string]:
      kind === "gumballs" ? palette.gumballFill : palette.coinFill,
    ["--jar-fill-glow" as string]:
      kind === "gumballs" ? palette.gumballGlow : palette.coinGlow
  };
}

function spriteStyle(
  sprite: (typeof SPRITE_LAYOUT)[number],
  index: number,
  kind: "coins" | "gumballs",
  palette: ThemeBankPalette
): CSSProperties {
  const color =
    kind === "gumballs"
      ? palette.gumballPalette[index % palette.gumballPalette.length]!
      : palette.coinBase;
  const depth = index % 3 === 0 ? 0.82 : index % 3 === 1 ? 0.94 : 1;
  return {
    left: `${sprite.x}%`,
    bottom: `${sprite.y}%`,
    width: `${Math.round(sprite.size * 0.76)}px`,
    height: `${Math.round(sprite.size * 0.76)}px`,
    animationDelay: `${sprite.delay}s`,
    ["--jar-color" as string]: color,
    ["--jar-depth" as string]: depth,
    ["--jar-rotate" as string]: `${sprite.rotate}deg`,
    ["--jar-rim" as string]: kind === "gumballs" ? palette.gumballRim : palette.coinRim
  };
}
